import * as Select from '@radix-ui/react-select'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { SupportCode } from '@/features/support-code'
import { ULR_QUERY_PARAM } from '@/features/url-handling'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { translate } from '@/utils/translator'

const PAYMENT_TYPES = ['visa', 'mastercard', 'paypal', 'apple-pay', 'klarna']

export const Footer = () => {
	const { navigation, languages } = useAppConfigStore.use.footerLinks()

	const onLanguageChange = (target: string) => {
		if (languages.filter((lang) => lang.url === target)[0]?.active) {
			return
		}

		const currentUrl = new URL(window.location.href)
		const targetUrl = new URL(target)

		targetUrl.searchParams.set(
			ULR_QUERY_PARAM,
			currentUrl.searchParams.get(ULR_QUERY_PARAM) ?? ''
		)

		window.location.href = targetUrl.toString()
	}

	return (
		<footer
			className={cn(
				'flex w-full flex-col items-center justify-center gap-40 bg-footer-bg px-24 py-64 text-naturel'
			)}
		>
			<div className="mx-auto w-full max-w-content">
				<div className="flex flex-row flex-wrap justify-between gap-x-[100px] gap-y-[80px]">
					<nav className={cn('flex flex-wrap gap-x-68 gap-y-32')}>
						{navigation.map(
							(item) =>
								item.items.length !== 0 && (
									<div className="" key={item.label}>
										<Typography element="p" style="p-ms-bold" className="mb-24">
											{item.label}
										</Typography>

										<div className="flex flex-col gap-y-8">
											{item.items.map((entry) => (
												<a
													key={entry.label}
													href={entry.url}
													className="hover:underline"
												>
													<Typography element="span" style="p-ms">
														{entry.label}
													</Typography>
												</a>
											))}
										</div>
									</div>
								)
						)}
					</nav>
					<div className="flex flex-row gap-x-68">
						<div>
							<Typography element="p" style="p-ms-bold" className="mb-24">
								{translate('footer-navigation-help')}
							</Typography>

							<div className="flex flex-col gap-y-8">
								<SupportCode />
							</div>
						</div>

						<div>
							<Typography element="p" style="p-ms-bold" className="mb-24">
								{translate('footer-navigation-language')}
							</Typography>
							<div className="flex flex-col gap-y-8">
								<a href="#test" className="hover:underline">
									{
										<Select.Root
											onValueChange={(value) => onLanguageChange(value)}
										>
											<Select.Trigger
												className={cn('flex w-full justify-between')}
											>
												<Select.Value
													placeholder={
														languages.filter((lang) => lang.active)[0]
															?.label
													}
												/>
												<SvgIcon
													collectionName="ibe"
													iconName="chevron"
													className="h-24 w-24 rotate-180 text-white"
												/>
											</Select.Trigger>
											<Select.Content
												position="popper"
												className={cn(
													'shadow-none z-10 w-full rounded-8 bg-naturel px-12 py-16'
												)}
											>
												<Select.Viewport
													className={cn('flex flex-col gap-8 text-black')}
												>
													{languages.map((lang) => (
														<Select.Item
															key={lang.url}
															value={lang.url}
															className="border-none"
														>
															<Typography element="span" style="p">
																{lang.label}
															</Typography>
														</Select.Item>
													))}
												</Select.Viewport>
											</Select.Content>
										</Select.Root>
									}
								</a>
							</div>
						</div>
					</div>
				</div>

				<hr className="mb-40 mt-32" />

				<div className="flex flex-wrap justify-between gap-y-32">
					<div>
						<SvgIcon iconName="dsr-hotel-holding" className={cn('h-40 w-[127px]')} />
					</div>
					<div className={cn('flex flex-wrap gap-x-16')}>
						{PAYMENT_TYPES.map((type) => (
							<SvgIcon
								key={type}
								iconName={type}
								collectionName="ibe3"
								className="h-32 w-48 text-naturel"
							/>
						))}
					</div>
				</div>

				<Typography
					element="p"
					style="p-s"
					className="mt-16 text-right uppercase text-naturel/50"
				>
					© {new Date().getFullYear()} DSR Hotel Holding GmbH
				</Typography>
			</div>
		</footer>
	)
}
